// Monitor page: agrega snapshots via BroadcastChannel("wuwei-agent-pool")
// (mesmo processo) E via relay SSE em :4174 (cross-process). Renderiza
// 3 tabelas: workers, processos, runs.
//
// Pode ser usado de 2 formas:
// 1. Standalone (monitor.html) — auto-mounts via initMonitor() no DOMContentLoaded
// 2. Embedded (tab no terminal) — chama initMonitor(container) explicitamente

import "./styles.css";
import { subscribe as relaySubscribe } from "../runtime/relay-client.ts";

const MONITOR_HTML = `
  <section class="mon-summary">
    <div><div class="mon-stat-label">Páginas conectadas</div><div class="mon-stat-value" id="stat-pages">0</div></div>
    <div><div class="mon-stat-label">Workers ativos</div><div class="mon-stat-value" id="stat-workers">0</div></div>
    <div><div class="mon-stat-label">Processos rodando</div><div class="mon-stat-value" id="stat-running">0</div></div>
    <div><div class="mon-stat-label">No histórico</div><div class="mon-stat-value" id="stat-history">0</div></div>
    <div><div class="mon-stat-label">Heap reportado</div><div class="mon-stat-value" id="stat-heap">—</div></div>
    <div><div class="mon-stat-label">Última atualização</div><div class="mon-stat-value" id="stat-updated">—</div></div>
    <div style="display:flex; align-items:end;">
      <button id="btn-clear" type="button" class="mon-clear-btn"
              onclick="(window).__monitorClear && window.__monitorClear()">Limpar histórico</button>
    </div>
  </section>
  <section class="mon-section">
    <h2>Workers</h2>
    <table class="mon-table">
      <thead><tr>
        <th>Página</th><th>Worker</th><th>Role</th><th>Uptime</th>
        <th>Última msg</th><th>Heap</th><th>Tools rodadas</th>
        <th>Modelos carregados</th><th>Status atual</th>
      </tr></thead>
      <tbody id="workers-tbody"><tr class="mon-empty"><td colspan="9">aguardando…</td></tr></tbody>
    </table>
  </section>
  <section class="mon-section">
    <h2>Processos (em execução + histórico)</h2>
    <table class="mon-table">
      <thead><tr>
        <th>Agente (invocation)</th><th>Worker</th><th>Processo</th>
        <th>Status</th><th>T. desde início</th><th>T. desde última msg</th>
        <th>Última mensagem</th><th>Memória ocupada</th>
      </tr></thead>
      <tbody id="procs-tbody"><tr class="mon-empty"><td colspan="8">aguardando…</td></tr></tbody>
    </table>
  </section>
  <section class="mon-section">
    <h2>Invocações de agentes</h2>
    <table class="mon-table">
      <thead><tr>
        <th>Página</th><th>Run</th><th>Invocation ID</th>
        <th>Agente</th><th>Arquivo</th><th>Status</th>
        <th>Duração</th><th>Tools</th><th>Erro</th>
      </tr></thead>
      <tbody id="runs-tbody"><tr class="mon-empty"><td colspan="9">aguardando…</td></tr></tbody>
    </table>
  </section>
`;

let monitorMounted = false;

/** Monta o monitor dentro de um container. Idempotente. */
export function initMonitor(container: HTMLElement): void {
  if (monitorMounted) return;
  container.innerHTML = MONITOR_HTML;
  monitorMounted = true;
  setupMonitorWiring();
}

interface ProcView {
  id: string; invocationId: string; toolId: string; role: string;
  workerId: string; status: string;
  startedAt: number; sinceStartMs: number; sinceLastMsgMs: number | null;
  lastStep: string; durationMs: number | null;
  heapSnapshot: number | null; error: string | null;
}
interface RunView {
  id: string; invocationId: string; agentId: string; fileName: string;
  status: string; startedAt: number; durationMs: number;
  procCount: number; error: string | null;
}
interface WorkerView {
  id: string; role: string; uptimeMs: number; silentForMs: number;
  heap: number | null; heapSource: string | null; toolsRun: number;
  loaded: string[]; currentToolId: string | null; lastStep: string | null;
}
interface Snapshot {
  name: string; pageId: string;
  workers: WorkerView[]; procs: ProcView[]; runs: RunView[];
}

const sources = new Map<string, { pageTitle: string; ts: number; snapshot: Snapshot }>();
const STALE_MS = 8000;   // sem msg por mais que isso → marca desconectado
const PERSIST_KEY = "wuwei-monitor-sources-v1";
const PERSIST_MAX_SOURCES = 20;        // cap pra não estourar localStorage
const PERSIST_MAX_BYTES = 2 * 1024 * 1024; // 2 MB cap (localStorage é 5MB total)
let lastUpdated: number | null = null;
let persistTimer: number | null = null;

function persistSoon(): void {
  if (persistTimer != null) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    persistNow();
  }, 500);
}

function persistNow(): void {
  try {
    // Mantém só os N mais recentes (por ts)
    const sorted = [...sources.entries()]
      .sort((a, b) => b[1].ts - a[1].ts)
      .slice(0, PERSIST_MAX_SOURCES);
    const payload = JSON.stringify({
      version: 1,
      ts: Date.now(),
      sources: sorted.map(([id, v]) => [id, v]),
    });
    if (payload.length > PERSIST_MAX_BYTES) {
      // Drops snapshots gigantes (vetores embedding > 1k floats)
      const slim = sorted.map(([id, v]) => [id, {
        pageTitle: v.pageTitle, ts: v.ts,
        snapshot: { ...v.snapshot, procs: (v.snapshot.procs || []).slice(0, 10) },
      }]);
      const slimJson = JSON.stringify({ version: 1, ts: Date.now(), sources: slim });
      localStorage.setItem(PERSIST_KEY, slimJson);
    } else {
      localStorage.setItem(PERSIST_KEY, payload);
    }
  } catch (e) {
    console.warn("[monitor] persist falhou:", e);
  }
}

function restorePersisted(): void {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const data = JSON.parse(raw) as {
      version: number;
      sources: Array<[string, { pageTitle: string; ts: number; snapshot: Snapshot }]>;
    };
    if (data.version !== 1) return;
    for (const [id, v] of data.sources) sources.set(id, v);
    console.info(`[monitor] restaurados ${sources.size} sources do localStorage`);
  } catch (e) {
    console.warn("[monitor] restore falhou:", e);
  }
}

restorePersisted();

function ingestSnapshot(data: {
  type?: string; pageId?: string; pageTitle?: string; ts?: number; snapshot?: Snapshot;
}, origin: "broadcast" | "relay"): void {
  if (data?.type !== "snapshot" || !data.pageId || !data.snapshot) return;
  const existing = sources.get(data.pageId);
  // Idempotente: se chegar via broadcast E relay, último ts vence.
  if (existing && existing.ts >= (data.ts ?? 0)) return;

  const pageTitle = (data.pageTitle ?? data.pageId) + (origin === "relay" ? " (relay)" : "");
  const now = Date.now();

  // Auto-cleanup: se este é um pageId NOVO e existem sources stale com mesmo
  // pageTitle, remove os stales antes de adicionar (reativa no mesmo "slot").
  if (!existing) {
    for (const [k, v] of sources) {
      if (k === data.pageId) continue;
      if (v.pageTitle === pageTitle && (now - v.ts) > STALE_MS) {
        sources.delete(k);
      }
    }
  }

  sources.set(data.pageId, {
    pageTitle,
    ts: data.ts ?? now,
    snapshot: data.snapshot,
  });
  lastUpdated = data.ts ?? now;
  render();
  persistSoon();
}

(window as unknown as Record<string, unknown>).__monitorClear = () => {
  sources.clear();
  lastUpdated = null;
  try { localStorage.removeItem(PERSIST_KEY); } catch { /* */ }
  render();
};

// Helpers expostos pra Playwright validar:
(window as unknown as Record<string, unknown>).__monitorState = () => ({
  sources: [...sources.entries()].map(([id, v]) => ({
    pageId: id, pageTitle: v.pageTitle, ts: v.ts,
    workerCount: v.snapshot.workers?.length ?? 0,
    procCount: v.snapshot.procs?.length ?? 0,
    runCount: v.snapshot.runs?.length ?? 0,
    isStale: (Date.now() - v.ts) > STALE_MS,
  })),
  lastUpdated,
});

let wiringDone = false;
let renderTimer: number | null = null;

function setupMonitorWiring(): void {
  if (wiringDone) return;
  wiringDone = true;
  const channel = new BroadcastChannel("wuwei-agent-pool");
  channel.onmessage = (e) => ingestSnapshot(e.data, "broadcast");
  channel.postMessage({ type: "request-snapshot" });
  relaySubscribe((data) => ingestSnapshot(
    data as Parameters<typeof ingestSnapshot>[0],
    "relay",
  ));
  renderTimer = window.setInterval(render, 1000);
  render();
}

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m${String(s).padStart(2, "0")}s`;
}

function fmtBytes(b: number | null | undefined): string {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

function statusCell(s: string): string {
  return `<span class="mon-status mon-status-${s}">${s}</span>`;
}

function escapeHtml(s: string | null | undefined): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function fmtSince(ms: number): string {
  if (ms < 1000) return "agora";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s atrás`;
  if (ms < 3600_000) return `${Math.round(ms / 60_000)}min atrás`;
  return `${Math.round(ms / 3600_000)}h atrás`;
}

function render(): void {
  const now = Date.now();
  // NÃO dropa sources stale — mantém visível pra histórico.
  // Apenas calcula `isStale` por source pra renderização.

  let totalWorkers = 0, totalRunning = 0, totalHistory = 0, totalHeap = 0;
  let totalLive = 0, totalStale = 0;
  let heapHas = false;
  const workerRows: string[] = [];
  const procRows: string[] = [];
  const runRows: string[] = [];

  for (const src of sources.values()) {
    const s = src.snapshot;
    if (!s) continue;
    const isStale = (now - src.ts) > STALE_MS;
    if (isStale) totalStale++; else totalLive++;
    const stalePrefix = isStale
      ? `<span class="mon-status mon-status-cancelled" title="última atividade ${fmtSince(now - src.ts)}">desconectado · ${fmtSince(now - src.ts)}</span> `
      : "";
    const titleHtml = `${stalePrefix}${escapeHtml(src.pageTitle)}`;
    const trCls = isStale ? "mon-source-stale" : "";

    for (const w of s.workers || []) {
      totalWorkers++;
      if (typeof w.heap === "number") { totalHeap += w.heap; heapHas = true; }
      const silentCls = w.silentForMs > 30000 ? "mon-warn" : w.silentForMs > 10000 ? "mon-mute" : "";
      workerRows.push(`
        <tr class="${trCls}">
          <td class="mon-page">${titleHtml}</td>
          <td><code>${escapeHtml(w.id)}</code></td>
          <td><span class="mon-role">${escapeHtml(w.role)}</span></td>
          <td>${fmtMs(w.uptimeMs)}</td>
          <td class="${silentCls}">${fmtMs(w.silentForMs)}</td>
          <td>${fmtBytes(w.heap)}${w.heapSource === "main" ? ' <span class="mon-mute" title="heap do main thread">·main</span>' : ""}</td>
          <td>${w.toolsRun}</td>
          <td class="mon-loaded">${(w.loaded || []).map(escapeHtml).join("<br>") || "—"}</td>
          <td>${w.currentToolId
            ? `<span class="mon-status mon-status-running">${escapeHtml(w.currentToolId)}</span>`
            : `<span class="mon-status mon-status-idle">idle</span>`}</td>
        </tr>`);
    }

    for (const p of s.procs || []) {
      if (p.status === "running") totalRunning++;
      else totalHistory++;
      const silentCls = p.sinceLastMsgMs == null ? ""
        : p.sinceLastMsgMs > 30000 ? "mon-warn"
        : p.sinceLastMsgMs > 10000 ? "mon-mute" : "";
      procRows.push(`
        <tr class="mon-proc-${p.status} ${trCls}">
          <td><code>${escapeHtml(p.invocationId.slice(0, 16))}</code></td>
          <td><code>${escapeHtml(p.workerId)}</code></td>
          <td>
            <code>${escapeHtml(p.id)}</code>
            <span class="mon-role">${escapeHtml(p.toolId)}</span>
          </td>
          <td>${statusCell(p.status)}</td>
          <td>${fmtMs(p.sinceStartMs)}</td>
          <td class="${silentCls}">${p.sinceLastMsgMs == null ? "—" : fmtMs(p.sinceLastMsgMs)}</td>
          <td class="mon-laststep">${escapeHtml(p.lastStep)}</td>
          <td>${fmtBytes(p.heapSnapshot)}</td>
        </tr>`);
    }

    for (const r of s.runs || []) {
      runRows.push(`
        <tr class="mon-run-${r.status} ${trCls}">
          <td class="mon-page">${titleHtml}</td>
          <td><code>${escapeHtml(r.id)}</code></td>
          <td><code>${escapeHtml(r.invocationId.slice(0, 16))}</code></td>
          <td><span class="mon-role">${escapeHtml(r.agentId)}</span></td>
          <td>${escapeHtml(r.fileName)}</td>
          <td>${statusCell(r.status)}</td>
          <td>${fmtMs(r.durationMs)}</td>
          <td>${r.procCount} tools</td>
          <td>${escapeHtml(r.error)}</td>
        </tr>`);
    }
  }

  document.getElementById("stat-pages")!.textContent = String(sources.size);
  document.getElementById("stat-workers")!.textContent = String(totalWorkers);
  document.getElementById("stat-running")!.textContent = String(totalRunning);
  document.getElementById("stat-history")!.textContent = String(totalHistory);
  document.getElementById("stat-heap")!.textContent = heapHas ? fmtBytes(totalHeap) : "—";
  document.getElementById("stat-updated")!.textContent =
    lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : "—";

  const wbody = document.getElementById("workers-tbody")!;
  wbody.innerHTML = workerRows.length ? workerRows.join("")
    : `<tr class="mon-empty"><td colspan="9">nenhum worker ativo. abra o terminal em outra aba e rode um agente.</td></tr>`;

  const pbody = document.getElementById("procs-tbody")!;
  pbody.innerHTML = procRows.length ? procRows.join("")
    : `<tr class="mon-empty"><td colspan="8">nenhum processo ainda.</td></tr>`;

  const rbody = document.getElementById("runs-tbody")!;
  rbody.innerHTML = runRows.length ? runRows.join("")
    : `<tr class="mon-empty"><td colspan="9">nenhuma invocação de agente ainda.</td></tr>`;
}

// Auto-mount em standalone (monitor.html, com classe .monitor-page no body).
// No embedded mode chamamos initMonitor(container) explicitamente.
if (typeof document !== "undefined" && document.body?.classList.contains("monitor-page")) {
  monitorMounted = true; // HTML já está no body
  setupMonitorWiring();
}
